import type { LocaleCode, PaymentStatus } from "@fiket/booth-schema";
import type { PaymentExitReason } from "../../store/router";

export type { PaymentExitReason };

/** 키오스크 폴링 간격 (스펙 §6: 2초) */
export const PAYMENT_POLL_INTERVAL_MS = 2_000;
/** 키오스크 타이머 3분 — 클라이언트 상대 시계. 서버 expiresAt 은 생성 + 3분 30초(PAYMENT_PENDING_TTL_MS) 로 뒤에 온다 (F33) */
export const PAYMENT_TIMER_MS = 180_000;
/** 마지막 성공 폴링 후 이 시간이 지나면 "연결 확인 중" 배너 — 타이머는 계속 간다 (F28) */
export const PAYMENT_STALE_POLL_MS = 10_000;
/** 남은 시간이 이 아래면 타이머를 강조색으로 */
export const PAYMENT_URGENT_MS = 30_000;

export function remainingMs(startedAtMs: number, nowMs: number): number {
  return Math.min(PAYMENT_TIMER_MS, Math.max(0, PAYMENT_TIMER_MS - (nowMs - startedAtMs)));
}

/** "mm:ss" — 초는 올림(1ms 남아도 00:01, 손님에게 0 이 먼저 보이지 않게) */
export function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function isUrgent(ms: number): boolean {
  return ms < PAYMENT_URGENT_MS;
}

/** 금액 표기 — ko "5,000원", 그 외 "₩5,000". 통화는 KRW 고정(해외 PG 는 후속 이슈) */
export function formatKrw(amountKrw: number, lang: LocaleCode): string {
  const digits = amountKrw.toLocaleString("en-US", { maximumFractionDigits: 0 });
  return lang === "ko" ? `${digits}원` : `₩${digits}`;
}

export type PaymentOutcome = "pending" | "paid" | "closed";

/** 서버 상태 → 키오스크가 할 일. Record 로 7종 전부 강제 — 상태가 늘면 컴파일 에러 */
const OUTCOME: Record<PaymentStatus, PaymentOutcome> = {
  pending: "pending",
  failed: "pending", // Toss 거절 — 손님이 폰에서 다시 시도할 수 있다 (스펙 §4.1)
  paid: "paid",
  partially_refunded: "paid",
  cancelled: "closed",
  expired: "closed",
  refunded: "closed",
};

export function paymentOutcome(status: PaymentStatus): PaymentOutcome {
  return OUTCOME[status];
}

/** 종결 상태 → 결과 화면에 보일 사유 */
export function exitReasonFor(status: PaymentStatus): PaymentExitReason {
  return status === "expired" ? "expired" : "cancelled";
}

/**
 * 취소 요청 결과 → 갈 곳. 409 ALREADY_PAID 는 타이머 만료와 승인이 겹친 것(F29) — 손님이 돈을 냈으니 paid 로 진행.
 * 네트워크 실패 등은 closed — 서버가 expiresAt(+30초) 에 스스로 만료시킨다 (F30).
 */
export function cancelOutcome(result: { ok: true } | { ok: false; code: string }): "paid" | "closed" {
  return !result.ok && result.code === "ALREADY_PAID" ? "paid" : "closed";
}

export type PaymentBanner = "none" | "connection" | "failed";

export function pollBanner(input: {
  status: PaymentStatus | null;
  nowMs: number;
  lastOkAtMs: number | null;
  startedAtMs: number;
}): PaymentBanner {
  const since = input.lastOkAtMs ?? input.startedAtMs;
  if (input.nowMs - since >= PAYMENT_STALE_POLL_MS) return "connection";
  if (input.status === "failed") return "failed";
  return "none";
}
